import React, { useState, useRef, useEffect, useMemo, Suspense, forwardRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Stars, Trail, RoundedBox } from '@react-three/drei';
import * as THREE from 'three';
import { Heart, Volume2, VolumeX, Rocket, Trophy } from 'lucide-react';
import * as Tone from 'tone';
import { getHighScore, trySetHighScore, GAME_KEYS } from '../utils/HighScoreManager';

// --- ZzFX Micro-Library (Embedded) ---
// ZzFX - Zuper Zmall Zound Zynth - Micro Edition
// MIT License - Copyright 2019 Frank Force
let zzfx, zzfxV, zzfxX;
zzfxV = 0.3; // Master Volume
zzfx = (p) => {
    if (!window.AudioContext) return;
    let x = zzfxX || (zzfxX = new (window.AudioContext || window.webkitAudioContext));
    let t = x.createBufferSource();
    let b = x.createBuffer(1, p.length, x.sampleRate);
    b.getChannelData(0).set(p);
    t.buffer = b;
    t.connect(x.destination);
    t.start();
    return t;
}
zzfx.g = (volume = 1, randomness = .05, frequency = 220, attack = 0, decay = 0, sustain = 0, release = .1, shape = 0, shapeCurve = 1, slide = 0, deltaSlide = 0, pitchJump = 0, pitchJumpTime = 0, repeatTime = 0, noise = 0, modulation = 0, bitCrush = 0, delay = 0, sustainVolume = 1, decayTime = 0, tremolo = 0) => {
    let PI2 = Math.PI * 2, sampleRate = 44100, sign = v => v > 0 ? 1 : -1, startSlide = slide *= 500 * PI2 / sampleRate / sampleRate, startFrequency = frequency *= (1 + randomness * 2 * Math.random() - randomness) * PI2 / sampleRate, b = [], t = 0, tm = 0, i = 0, j = 1, r = 0, c = 0, s = 0, f, length;
    attack = attack * sampleRate + 9; decay = decay * sampleRate; sustain = sustain * sampleRate; release = release * sampleRate; delay = delay * sampleRate + 9;
    for (length = attack + decay + sustain + release + delay | 0; i < length; b[i++] = s) {
        if (++c > (100 * PI2 / (sampleRate * frequency)) && sign(f) != sign(s) && (s = sign(f), c = 0), i < delay) s = 0;
        else {
            if (f = startFrequency, slide += deltaSlide * 500 * PI2 / sampleRate / sampleRate / sampleRate, f += slide, f *= 1 + modulation * Math.sin(tm * PI2), t += f, f = startFrequency, bitCrush && (t = Math.round(t / bitCrush) * bitCrush), noise && (f = (1 - noise) * Math.sin(t) + noise * (Math.random() * 2 - 1)), s = (1 - tremolo * Math.sin(tm * PI2)) * Math.sign(f) * Math.pow(Math.abs(f), shapeCurve), i < attack + decay + sustain) s *= sustainVolume;
            else s *= sustainVolume * Math.pow(1 - (i - attack - decay - sustain) / release, 2);
            s = Math.max(Math.min(s, 1), -1);
            s = (i < attack ? i / attack : i < attack + decay ? 1 - (i - attack) / decay * (1 - sustainVolume) : 1) * s * volume;
            if (delay) s = s * .5 + (i >= delay ? b[i - delay] : 0);
        }
        tm += 6.28 / sampleRate;
    }
    return b;
}

// --- Sound Definitions (16-Bit Style) ---
// --- Sound Definitions (Tone.js) ---
// ZzFX removed. All sounds now handled by Tone.js in useToneSfx hook.

// --- Constants ---
const PLAYER_SPEED = 0.1;
const BULLET_SPEED = 0.6;
const ENEMY_SPEED = 0.15;
const ENEMY_BULLET_SPEED = 0.2;
const SPAWN_RATE = 60;
const ORB_CHANCE = 0.2;
const HEALTH_PICKUP_CHANCE = 0.05;
const WEAPON_LEVELS = ['SINGLE', 'DOUBLE', 'LASER', 'TRIPLE'];

// --- Models ---

const PlayerShip = forwardRef(({ position }, ref) => {
    const { viewport } = useThree();

    return (
        <group ref={ref} position={position} scale={[0.5, 0.5, 0.5]}>
            {/* Main Fuselage - Silver/Metallic */}
            <mesh rotation={[Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.2, 0.5, 3, 8]} />
                <meshStandardMaterial color="#cbd5e1" metalness={0.8} roughness={0.2} />
            </mesh>

            {/* Cockpit Canopy - Blue Glass */}
            <mesh position={[0, 0.3, -0.2]} rotation={[0.2, 0, 0]}>
                <capsuleGeometry args={[0.25, 1, 4, 8]} />
                <meshStandardMaterial color="#3b82f6" metalness={0.9} roughness={0.1} opacity={0.9} transparent />
            </mesh>

            {/* Wings - Swept Back */}
            <group position={[0, 0, 0.5]}>
                <mesh position={[1.2, 0, 0]} rotation={[0, 0, -0.2]}>
                    <boxGeometry args={[2, 0.1, 1.5]} />
                    <meshStandardMaterial color="#94a3b8" metalness={0.7} roughness={0.3} />
                </mesh>
                <mesh position={[-1.2, 0, 0]} rotation={[0, 0, 0.2]}>
                    <boxGeometry args={[2, 0.1, 1.5]} />
                    <meshStandardMaterial color="#94a3b8" metalness={0.7} roughness={0.3} />
                </mesh>

                {/* Glowing Accents on Wings */}
                <mesh position={[1.2, 0.06, 0]} rotation={[0, 0, -0.2]}>
                    <boxGeometry args={[1.8, 0.02, 0.1]} />
                    <meshStandardMaterial color="#22d3ee" emissive="#06b6d4" emissiveIntensity={2} />
                </mesh>
                <mesh position={[-1.2, 0.06, 0]} rotation={[0, 0, 0.2]}>
                    <boxGeometry args={[1.8, 0.02, 0.1]} />
                    <meshStandardMaterial color="#22d3ee" emissive="#06b6d4" emissiveIntensity={2} />
                </mesh>
            </group>

            {/* Twin Engines */}
            <group position={[0, 0, 1.2]}>
                <mesh position={[0.6, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
                    <cylinderGeometry args={[0.25, 0.2, 1.5, 8]} />
                    <meshStandardMaterial color="#64748b" metalness={0.8} />
                </mesh>
                <mesh position={[-0.6, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
                    <cylinderGeometry args={[0.25, 0.2, 1.5, 8]} />
                    <meshStandardMaterial color="#64748b" metalness={0.8} />
                </mesh>

                {/* Engine Glows */}
                <mesh position={[0.6, 0, 0.8]}>
                    <sphereGeometry args={[0.2]} />
                    <meshBasicMaterial color="#3b82f6" />
                </mesh>
                <mesh position={[-0.6, 0, 0.8]}>
                    <sphereGeometry args={[0.2]} />
                    <meshBasicMaterial color="#3b82f6" />
                </mesh>
            </group>

            {/* Trails - Twin */}
            <group position={[0.6, 0, 2]}>
                <Trail width={0.6} length={4} color="#3b82f6" attenuation={(t) => t * t}><mesh visible={false} /></Trail>
            </group>
            <group position={[-0.6, 0, 2]}>
                <Trail width={0.6} length={4} color="#3b82f6" attenuation={(t) => t * t}><mesh visible={false} /></Trail>
            </group>
        </group>
    );
});

const EnemyShip = forwardRef((props, ref) => {
    // Flash logic is handled by the main loop now, so we just render the mesh.
    return (
        <group ref={ref} rotation={[0, Math.PI, 0]} scale={[1.2, 1.2, 1.2]} {...props}>
            {/* Core */}
            <mesh>
                <octahedronGeometry args={[0.5]} />
                <meshStandardMaterial color="#7f1d1d" metalness={0.8} roughness={0.1} />
            </mesh>
            {/* Wings */}
            <mesh position={[0.6, 0, 0]} rotation={[0, 0, -0.5]}>
                <boxGeometry args={[0.8, 0.1, 0.5]} />
                <meshStandardMaterial color="#991b1b" metalness={0.6} />
            </mesh>
            <mesh position={[-0.6, 0, 0]} rotation={[0, 0, 0.5]}>
                <boxGeometry args={[0.8, 0.1, 0.5]} />
                <meshStandardMaterial color="#991b1b" metalness={0.6} />
            </mesh>
            {/* Prongs */}
            <mesh position={[0.4, 0, 0.2]} rotation={[0, 0, -0.5]}>
                <coneGeometry args={[0.15, 1, 4]} />
                <meshStandardMaterial color="#ef4444" />
            </mesh>
            <mesh position={[-0.4, 0, 0.2]} rotation={[0, 0, 0.5]}>
                <coneGeometry args={[0.15, 1, 4]} />
                <meshStandardMaterial color="#ef4444" />
            </mesh>
            <mesh position={[0, 0.4, 0.2]} rotation={[0.5, 0, 0]}>
                <coneGeometry args={[0.15, 1, 4]} />
                <meshStandardMaterial color="#ef4444" />
            </mesh>
            <mesh position={[0, -0.4, 0.2]} rotation={[-0.5, 0, 0]}>
                <coneGeometry args={[0.15, 1, 4]} />
                <meshStandardMaterial color="#ef4444" />
            </mesh>
            {/* Cockpit Window - Neon Teal Blue */}
            <mesh position={[0, 0.1, -0.35]} rotation={[0.2, 0, 0]}>
                <boxGeometry args={[0.3, 0.2, 0.1]} />
                <meshStandardMaterial color="#2dd4bf" emissive="#0d9488" emissiveIntensity={2} toneMapped={false} />
            </mesh>
            {/* Engine */}
            <mesh position={[0, 0, 0.5]}>
                <sphereGeometry args={[0.2]} />
                <meshBasicMaterial color="#f97316" />
            </mesh>
        </group>
    );
});

const PlusSign = forwardRef(({ color, rainbow, outline }, ref) => {
    const localRef = useRef();

    // Expose localRef to parent ref
    React.useImperativeHandle(ref, () => localRef.current);

    useFrame((state) => {
        if (localRef.current) {
            localRef.current.rotation.z += 0.05;
            localRef.current.rotation.y += 0.02;
            if (rainbow) {
                const hue = (state.clock.elapsedTime * 0.5) % 1;
                localRef.current.children.forEach(child => {
                    if (child.isMesh) {
                        child.material.color.setHSL(hue, 1, 0.5);
                        child.material.emissive.setHSL(hue, 1, 0.2);
                    }
                });
            }
        }
    });

    return (
        <group ref={localRef}>
            <mesh>
                <boxGeometry args={[0.8, 0.25, 0.1]} />
                <meshStandardMaterial
                    color={color}
                    emissive={color}
                    emissiveIntensity={outline ? 2 : 0.5}
                    wireframe={outline}
                />
            </mesh>
            <mesh>
                <boxGeometry args={[0.25, 0.8, 0.1]} />
                <meshStandardMaterial
                    color={color}
                    emissive={color}
                    emissiveIntensity={outline ? 2 : 0.5}
                    wireframe={outline}
                />
            </mesh>
        </group>
    );
});

// --- Boss Parts ---
const BossPart = forwardRef(({ type }, ref) => {
    return (
        <group ref={ref} scale={[0.7, 0.7, 0.7]}>
            {type === 'BOSS_TOP' && (
                // Speech Bubble (The Group Chat) - NOW GREEN
                <group>
                    <mesh position={[0, 0, 0]}>
                        <sphereGeometry args={[1.5, 32, 32]} />
                        <meshStandardMaterial color="#22c55e" emissive="#15803d" emissiveIntensity={0.5} />
                    </mesh>
                    <mesh position={[1, -1, 0]} rotation={[0, 0, -0.5]}>
                        <coneGeometry args={[0.5, 1.5, 32]} />
                        <meshStandardMaterial color="#22c55e" emissive="#15803d" emissiveIntensity={0.5} />
                    </mesh>
                </group>
            )}
            {type === 'BOSS_CENTER' && (
                // Laptop (The Work) - NOW LAPTOP
                <group rotation={[0.5, 0, 0]}>
                    {/* Base */}
                    <mesh position={[0, -1, 0]}>
                        <boxGeometry args={[4, 0.2, 2.5]} />
                        <meshStandardMaterial color="#94a3b8" metalness={0.8} roughness={0.2} />
                    </mesh>
                    {/* Screen */}
                    <mesh position={[0, 1, -1.2]} rotation={[-0.2, 0, 0]}>
                        <boxGeometry args={[4, 2.5, 0.2]} />
                        <meshStandardMaterial color="#1e293b" metalness={0.9} roughness={0.1} />
                    </mesh>
                    {/* Screen Glow */}
                    <mesh position={[0, 1, -1.09]} rotation={[-0.2, 0, 0]}>
                        <planeGeometry args={[3.6, 2.1]} />
                        <meshBasicMaterial color="#3b82f6" opacity={0.8} transparent />
                    </mesh>
                </group>
            )}
            {type === 'BOSS_BOTTOM' && (
                // Hashtag (The Socials)
                <group>
                    <mesh position={[-0.5, 0, 0]}>
                        <boxGeometry args={[0.3, 3, 0.3]} />
                        <meshStandardMaterial color="#ec4899" emissive="#be185d" emissiveIntensity={0.5} />
                    </mesh>
                    <mesh position={[0.5, 0, 0]}>
                        <boxGeometry args={[0.3, 3, 0.3]} />
                        <meshStandardMaterial color="#ec4899" emissive="#be185d" emissiveIntensity={0.5} />
                    </mesh>
                    <mesh position={[0, 0.5, 0.2]} rotation={[0, 0, Math.PI / 2]}>
                        <boxGeometry args={[0.3, 3, 0.3]} />
                        <meshStandardMaterial color="#ec4899" emissive="#be185d" emissiveIntensity={0.5} />
                    </mesh>
                    <mesh position={[0, -0.5, 0.2]} rotation={[0, 0, Math.PI / 2]}>
                        <boxGeometry args={[0.3, 3, 0.3]} />
                        <meshStandardMaterial color="#ec4899" emissive="#be185d" emissiveIntensity={0.5} />
                    </mesh>
                </group>
            )}
        </group>
    );
});

const Bullet = forwardRef(({ type }, ref) => (
    <mesh ref={ref} rotation={[0, 0, type === 'LASER' ? 0 : Math.PI / 4]}>
        {type === 'LASER' ? (
            <boxGeometry args={[0.15, 0.15, 3]} />
        ) : (
            <boxGeometry args={[0.15, 0.15, 0.8]} />
        )}
        <meshBasicMaterial color={type === 'LASER' ? '#3b82f6' : '#d8b4fe'} />
    </mesh>
));

const EnemyBullet = forwardRef(({ type }, ref) => (
    <group ref={ref}>
        {type === 'BOSS_BUBBLE' ? (
            // Text Message Bubble - Improved
            <group>
                <RoundedBox args={[0.8, 0.5, 0.1]} radius={0.1} smoothness={4}>
                    <meshStandardMaterial color="#e5e7eb" />
                </RoundedBox>
                {/* Tail */}
                <mesh position={[0.25, -0.3, 0]} rotation={[0, 0, -0.5]}>
                    <coneGeometry args={[0.15, 0.4, 3]} />
                    <meshStandardMaterial color="#e5e7eb" />
                </mesh>
                {/* Text Lines */}
                <mesh position={[0, 0.1, 0.06]}>
                    <boxGeometry args={[0.5, 0.05, 0.01]} />
                    <meshBasicMaterial color="#9ca3af" />
                </mesh>
                <mesh position={[0, -0.05, 0.06]}>
                    <boxGeometry args={[0.3, 0.05, 0.01]} />
                    <meshBasicMaterial color="#9ca3af" />
                </mesh>
            </group>
        ) : type === 'BOSS_HASHTAG' ? (
            // Real Hashtag Shape
            <group scale={[0.5, 0.5, 0.5]}>
                <mesh position={[-0.2, 0, 0]}><boxGeometry args={[0.1, 1, 0.1]} /><meshBasicMaterial color="#ec4899" /></mesh>
                <mesh position={[0.2, 0, 0]}><boxGeometry args={[0.1, 1, 0.1]} /><meshBasicMaterial color="#ec4899" /></mesh>
                <mesh position={[0, 0.2, 0]} rotation={[0, 0, Math.PI / 2]}><boxGeometry args={[0.1, 1, 0.1]} /><meshBasicMaterial color="#ec4899" /></mesh>
                <mesh position={[0, -0.2, 0]} rotation={[0, 0, Math.PI / 2]}><boxGeometry args={[0.1, 1, 0.1]} /><meshBasicMaterial color="#ec4899" /></mesh>
            </group>
        ) : (
            // Envelope (Email)
            <group>
                {/* Border (Black Background) */}
                <mesh position={[0, 0, -0.01]}>
                    <boxGeometry args={[0.6, 0.4, 0.05]} />
                    <meshBasicMaterial color="#000000" />
                </mesh>
                {/* Body (White) */}
                <mesh position={[0, 0, 0]}>
                    <boxGeometry args={[0.5, 0.3, 0.06]} />
                    <meshBasicMaterial color="#ffffff" />
                </mesh>
                {/* Flap Lines (V Shape) */}
                <mesh position={[-0.12, 0.05, 0.04]} rotation={[0, 0, -0.6]}>
                    <boxGeometry args={[0.35, 0.02, 0.01]} />
                    <meshBasicMaterial color="#000000" />
                </mesh>
                <mesh position={[0.12, 0.05, 0.04]} rotation={[0, 0, 0.6]}>
                    <boxGeometry args={[0.35, 0.02, 0.01]} />
                    <meshBasicMaterial color="#000000" />
                </mesh>
            </group>
        )}
    </group>
));

const DoomScroller = forwardRef(({ difficulty }, ref) => {
    return (
        <group ref={ref} scale={[0.8, 0.8, 0.8]}>
            {/* Main Body - Smartphone Shape */}
            <RoundedBox args={[1.8, 3.5, 0.2]} radius={0.1} smoothness={4}>
                <meshStandardMaterial color="#1e293b" metalness={0.8} roughness={0.2} />
            </RoundedBox>

            {/* Screen - Black Mirror */}
            <mesh position={[0, 0, 0.11]}>
                <planeGeometry args={[1.6, 3.3]} />
                <meshStandardMaterial color="#000000" metalness={0.9} roughness={0.05} />
            </mesh>

            {/* Camera Notch */}
            <mesh position={[0, 1.5, 0.12]}>
                <capsuleGeometry args={[0.05, 0.2, 4, 8]} />
                <meshBasicMaterial color="#333" />
            </mesh>

            {/* Content Feed Lines (Decorative) */}
            <group position={[0, 0, 0.12]}>
                {[0.8, 0.4, 0, -0.4, -0.8].map((y, i) => (
                    <mesh key={i} position={[0, y, 0]}>
                        <planeGeometry args={[1.2, 0.2]} />
                        <meshBasicMaterial color="#334155" opacity={0.5} transparent />
                    </mesh>
                ))}
            </group>

            {/* Notification Badge (Glowing Red) */}
            <mesh position={[0.7, 1.6, 0.15]}>
                <circleGeometry args={[0.15, 32]} />
                <meshBasicMaterial color="#ef4444" />
            </mesh>
            <mesh position={[0.7, 1.6, 0.16]}>
                <circleGeometry args={[0.1, 32]} />
                <meshBasicMaterial color="#ffffff" />
            </mesh>
        </group>
    );
});

const Particle = forwardRef(({ color }, ref) => (
    <mesh ref={ref}>
        <boxGeometry args={[0.2, 0.2, 0.2]} />
        <meshBasicMaterial color={color} transparent />
    </mesh>
));

// --- Music Hook ---
const useMusic = (isMuted) => {
    const isLoaded = useRef(false);
    const synths = useRef({});
    const sequences = useRef({});
    const currentMode = useRef('NORMAL');

    useEffect(() => {
        // Initialize Synths
        const kick = new Tone.MembraneSynth({
            pitchDecay: 0.05,
            octaves: 4,
            oscillator: { type: "sine" },
            envelope: { attack: 0.001, decay: 0.4, sustain: 0.01, release: 1.4, attackCurve: "exponential" }
        }).toDestination();
        kick.volume.value = -6;

        const bass = new Tone.MonoSynth({
            oscillator: { type: "sawtooth" },
            envelope: { attack: 0.01, decay: 0.2, sustain: 0.2, release: 0.2 },
            filterEnvelope: { attack: 0.01, decay: 0.2, sustain: 0.2, release: 0.2, baseFrequency: 200, octaves: 2.5, exponent: 2 }
        }).toDestination();
        bass.volume.value = -12;

        const hats = new Tone.MetalSynth({
            frequency: 200, envelope: { attack: 0.001, decay: 0.1, release: 0.01 },
            harmonicity: 5.1, modulationIndex: 32, resonance: 4000, octaves: 1.5
        }).toDestination();
        hats.volume.value = -20;

        const lead = new Tone.PolySynth(Tone.Synth, {
            oscillator: { type: "fatsawtooth", count: 3, spread: 30 },
            envelope: { attack: 0.01, decay: 0.1, sustain: 0.5, release: 0.4 }
        }).toDestination();
        lead.volume.value = -18;

        const delay = new Tone.PingPongDelay("8n", 0.2).toDestination();
        const reverb = new Tone.Reverb({ decay: 2, wet: 0.3 }).toDestination();
        lead.connect(delay);
        lead.connect(reverb);

        synths.current = { kick, bass, hats, lead };
        isLoaded.current = true;

        // --- Normal Mode Sequences ---
        const normalKick = new Tone.Sequence((time, note) => {
            kick.triggerAttackRelease(note, "8n", time);
        }, ["C2", "C2", "C2", "C2"], "4n");

        const normalBass = new Tone.Sequence((time, note) => {
            if (note) bass.triggerAttackRelease(note, "16n", time);
        }, ["F2", ["F2", "F2"], null, "F2", "F2", "F2", null, ["F2", "G2"]], "4n");

        const normalHats = new Tone.Sequence((time) => {
            hats.triggerAttackRelease("32n", time, 0.3);
        }, [null, "C4"], "8n");

        const normalLead = new Tone.Sequence((time, note) => {
            if (note) lead.triggerAttackRelease(note, "16n", time);
        }, [
            ["F4", "A4"], null, ["C5", "E5"], null,
            ["F4", "A4"], ["G4", "B4"], null, ["C5", "E5"]
        ], "4n");

        // --- Boss Mode Sequences (Upbeat/Driving) ---
        const bossKick = new Tone.Sequence((time, note) => {
            kick.triggerAttackRelease(note, "8n", time);
        }, ["C2", "C2", "C2", "C2"], "4n"); // Driving 4/4

        const bossBass = new Tone.Sequence((time, note) => {
            if (note) bass.triggerAttackRelease(note, "16n", time);
        }, ["F2", "F2", "F2", "F2", "G2", "G2", "G2", "G2"], "8n"); // Driving 8ths

        const bossHats = new Tone.Sequence((time) => {
            hats.triggerAttackRelease("32n", time, 0.5);
        }, ["C4", "C4", "C4", "C4"], "16n"); // Fast 16ths

        const bossLead = new Tone.Sequence((time, note) => {
            if (note) lead.triggerAttackRelease(note, "16n", time);
        }, [
            "C5", "E5", "G5", "C6", "G5", "E5", "C5", "G4",
            "D5", "F5", "A5", "D6", "A5", "F5", "D5", "A4"
        ], "8n"); // Fast Arpeggio

        sequences.current = {
            normal: { kick: normalKick, bass: normalBass, hats: normalHats, lead: normalLead },
            boss: { kick: bossKick, bass: bossBass, hats: bossHats, lead: bossLead }
        };

        // Start Normal Mode by default
        normalKick.start(0);
        normalBass.start(0);
        normalHats.start("4m");
        normalLead.start("8m");

        Tone.Transport.bpm.value = 138;

        return () => {
            Tone.Transport.stop();
            Tone.Transport.cancel();
            Object.values(sequences.current.normal).forEach(s => s.dispose());
            Object.values(sequences.current.boss).forEach(s => s.dispose());
            kick.dispose();
            bass.dispose();
            hats.dispose();
            lead.dispose();
            delay.dispose();
            reverb.dispose();
        };
    }, []);

    useEffect(() => {
        Tone.Destination.mute = isMuted;
    }, [isMuted]);

    const setMusicMode = (mode) => {
        if (!isLoaded.current || mode === currentMode.current) return;

        const now = Tone.now();
        const nextBar = Tone.Transport.nextSubdivision("1m"); // Switch at next bar

        if (mode === 'BOSS') {
            // Ramp up tempo
            Tone.Transport.bpm.rampTo(150, 2);

            // Stop Normal
            Object.values(sequences.current.normal).forEach(s => s.stop(nextBar));

            // Start Boss
            Object.values(sequences.current.boss).forEach(s => s.start(nextBar));

        } else {
            // Ramp down tempo
            Tone.Transport.bpm.rampTo(138, 2);

            // Stop Boss
            Object.values(sequences.current.boss).forEach(s => s.stop(nextBar));

            // Start Normal
            Object.values(sequences.current.normal).forEach(s => s.start(nextBar));
        }
        currentMode.current = mode;
    };

    const startMusic = async () => {
        if (!isLoaded.current) return;
        await Tone.start();
        Tone.Transport.stop();
        Tone.Transport.position = 0;
        Tone.Transport.start();
    };

    const stopMusic = () => {
        Tone.Transport.stop();
    };

    return { startMusic, stopMusic, setMusicMode };
    return { startMusic, stopMusic };
};

// --- Tone.js SFX Hook ---
const useToneSfx = (isMuted) => {
    const explosionSynth = useRef(null);
    const laserSynth = useRef(null);
    const pickupSynth = useRef(null);
    const bossExplosionSynth = useRef(null);
    const hitSynth = useRef(null);

    // New Synths
    const damageSynth = useRef(null);
    const enemyHitSynth = useRef(null);
    const enemyShootSynth = useRef(null);
    const bossSpawnSynth = useRef(null);

    const isLoaded = useRef(false);

    useEffect(() => {
        // Explosion: Pink Noise -> Lowpass Filter (Soft Thud)
        const filter = new Tone.Filter(800, "lowpass").toDestination();
        explosionSynth.current = new Tone.NoiseSynth({
            noise: { type: "pink" },
            envelope: {
                attack: 0.001,
                decay: 0.3,
                sustain: 0,
                release: 0.3
            }
        }).connect(filter);

        // Laser: Triangle Wave (Musical Zap)
        laserSynth.current = new Tone.PolySynth(Tone.Synth, {
            oscillator: { type: "triangle" },
            envelope: {
                attack: 0.001,
                decay: 0.1,
                sustain: 0,
                release: 0.1
            }
        }).toDestination();
        laserSynth.current.volume.value = -10;

        // Pickup: High Sine Ping (Subtle)
        pickupSynth.current = new Tone.PolySynth(Tone.Synth, {
            oscillator: { type: "sine" },
            envelope: {
                attack: 0.001,
                decay: 0.1,
                sustain: 0,
                release: 0.5
            }
        }).toDestination();
        pickupSynth.current.volume.value = -5;

        // Hit: Soft Digital Blip (Glass Tapping)
        hitSynth.current = new Tone.PolySynth(Tone.Synth, {
            oscillator: { type: "sine" },
            envelope: {
                attack: 0.005,
                decay: 0.1,
                sustain: 0,
                release: 0.1
            }
        }).toDestination();
        hitSynth.current.volume.value = -12; // Much quieter, sits behind music

        // Boss Explosion: Deep Brown Noise (Rumble)
        const bossFilter = new Tone.Filter(200, "lowpass").toDestination();
        bossExplosionSynth.current = new Tone.NoiseSynth({
            noise: { type: "brown" },
            envelope: {
                attack: 0.01,
                decay: 1.0,
                sustain: 0,
                release: 1.0
            }
        }).connect(bossFilter);

        // --- New Synths ---

        // Damage: Low Glitchy Impact (FMSynth)
        damageSynth.current = new Tone.FMSynth({
            harmonicity: 3,
            modulationIndex: 10,
            oscillator: { type: "square" },
            envelope: { attack: 0.001, decay: 0.2, sustain: 0, release: 0.2 },
            modulation: { type: "triangle" },
            modulationEnvelope: { attack: 0.001, decay: 0.2, sustain: 0, release: 0.2 }
        }).toDestination();
        damageSynth.current.volume.value = -5;

        // Enemy Hit: Soft High Blip (Triangle)
        enemyHitSynth.current = new Tone.PolySynth(Tone.Synth, {
            oscillator: { type: "triangle" },
            envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.05 }
        }).toDestination();
        enemyHitSynth.current.volume.value = -10;

        // Enemy Shoot: Sci-Fi Laser Pew (FM)
        enemyShootSynth.current = new Tone.FMSynth({
            harmonicity: 8,
            modulationIndex: 15,
            oscillator: { type: "sine" },
            envelope: { attack: 0.001, decay: 0.2, sustain: 0, release: 0.1 },
            modulation: { type: "square" },
            modulationEnvelope: { attack: 0.001, decay: 0.1, sustain: 0, release: 0.1 }
        }).toDestination();
        enemyShootSynth.current.volume.value = -12;

        // Boss Spawn: Menacing Drone (PolySaw)
        bossSpawnSynth.current = new Tone.PolySynth(Tone.Synth, {
            oscillator: { type: "sawtooth" },
            envelope: { attack: 1, decay: 0.5, sustain: 0.5, release: 2 }
        }).toDestination();
        bossSpawnSynth.current.volume.value = -10;

        isLoaded.current = true;

        return () => {
            if (explosionSynth.current) explosionSynth.current.dispose();
            if (laserSynth.current) laserSynth.current.dispose();
            if (pickupSynth.current) pickupSynth.current.dispose();
            if (hitSynth.current) hitSynth.current.dispose();
            if (bossExplosionSynth.current) bossExplosionSynth.current.dispose();
            if (damageSynth.current) damageSynth.current.dispose();
            if (enemyHitSynth.current) enemyHitSynth.current.dispose();
            if (enemyShootSynth.current) enemyShootSynth.current.dispose();
            if (bossSpawnSynth.current) bossSpawnSynth.current.dispose();
            if (filter) filter.dispose();
            if (bossFilter) bossFilter.dispose();
        };
    }, []);

    useEffect(() => {
        const vol = isMuted ? -Infinity : -5;
        if (explosionSynth.current) explosionSynth.current.volume.value = vol;
        if (laserSynth.current) laserSynth.current.volume.value = isMuted ? -Infinity : -10;
        if (pickupSynth.current) pickupSynth.current.volume.value = vol;
        if (hitSynth.current) hitSynth.current.volume.value = isMuted ? -Infinity : -12;
        if (bossExplosionSynth.current) bossExplosionSynth.current.volume.value = vol;
        if (damageSynth.current) damageSynth.current.volume.value = vol;
        if (enemyHitSynth.current) enemyHitSynth.current.volume.value = isMuted ? -Infinity : -10;
        if (enemyShootSynth.current) enemyShootSynth.current.volume.value = isMuted ? -Infinity : -12;
        if (bossSpawnSynth.current) bossSpawnSynth.current.volume.value = isMuted ? -Infinity : -10;
    }, [isMuted]);

    const playExplosion = () => {
        if (!isLoaded.current || isMuted) return;
        // Fix: Prevent "Start time must be strictly greater" error by scheduling slightly in future if needed
        // and catching potential overlap errors for monophonic NoiseSynth
        try {
            const now = Tone.now();
            explosionSynth.current.triggerAttackRelease("8n", now);
        } catch (e) {
            // Ignore overlap errors
        }
    };

    const playLaser = () => {
        if (!isLoaded.current || isMuted) return;
        laserSynth.current.triggerAttackRelease("C5", "16n");
    };

    const playPickup = () => {
        if (!isLoaded.current || isMuted) return;
        pickupSynth.current.triggerAttackRelease("C6", "32n");
    };

    const playHit = () => {
        if (!isLoaded.current || isMuted) return;
        // Randomize velocity for texture (0.5 to 0.7)
        hitSynth.current.triggerAttackRelease("G5", "32n", "+0", Math.random() * 0.2 + 0.5);
    };

    const playBossExplosion = () => {
        if (!isLoaded.current || isMuted) return;
        try {
            const now = Tone.now();
            bossExplosionSynth.current.triggerAttackRelease("4n", now);
        } catch (e) { }
    };

    const playDamage = () => {
        if (!isLoaded.current || isMuted) return;
        damageSynth.current.triggerAttackRelease("C2", "8n");
    };

    const playEnemyHit = () => {
        if (!isLoaded.current || isMuted) return;
        enemyHitSynth.current.triggerAttackRelease("C6", "32n");
    };

    const playEnemyShoot = () => {
        if (!isLoaded.current || isMuted) return;
        enemyShootSynth.current.triggerAttackRelease("C5", "16n");
    };

    const playBossSpawn = () => {
        if (!isLoaded.current || isMuted) return;
        bossSpawnSynth.current.triggerAttackRelease(["C2", "G2", "C3"], "2n");
    };

    return { playExplosion, playLaser, playPickup, playHit, playBossExplosion, playDamage, playEnemyHit, playEnemyShoot, playBossSpawn };
};

// --- Background Scenery Manager ---
class BackgroundSceneryManager {
    constructor() {
        this.activeObjects = [];
        this.spawnTimer = 0;
        this.maxActiveObjects = 12;
        this.nextSpawnTime = 0;

        // Initial Spawn
        for (let i = 0; i < 5; i++) {
            this.spawnObject(true);
        }
    }

    update(dt, isBossActive) {
        // 1. Move existing objects
        this.activeObjects.forEach(obj => {
            obj.x += obj.vx * dt;
            obj.y += obj.vy * dt;
            if (obj.rotationSpeed) {
                obj.rotation.x += obj.rotationSpeed.x * dt;
                obj.rotation.y += obj.rotationSpeed.y * dt;
            }
        });

        // 2. Remove off-screen objects
        this.activeObjects = this.activeObjects.filter(obj => {
            return obj.x > -30 && obj.x < 30 && obj.y > -5 && obj.y < 25;
        });

        // 3. Spawn new objects
        if (!isBossActive) {
            this.spawnTimer += dt;
            if (this.spawnTimer >= this.nextSpawnTime && this.activeObjects.length < this.maxActiveObjects) {
                this.spawnObject();
                this.spawnTimer = 0;
                this.nextSpawnTime = 5 + Math.random() * 10;
            }
        }
    }

    spawnObject(initial = false) {
        // Removed nebulaCloud as it looked like a flat circle
        const types = ['planetSmall', 'planetLarge', 'ringedPlanet', 'moon', 'sunGlow', 'asteroid'];
        const weights = [0.25, 0.15, 0.15, 0.25, 0.05, 0.15];
        const totalWeight = weights.reduce((a, b) => a + b, 0);
        let random = Math.random() * totalWeight;
        let type = 'planetSmall';
        for (let i = 0; i < types.length; i++) {
            if (random < weights[i]) {
                type = types[i];
                break;
            }
            random -= weights[i];
        }

        const id = `bg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const parallax = 0.1 + Math.random() * 0.4;
        const speed = -0.5 * parallax;

        const x = initial ? (Math.random() - 0.5) * 50 : 25;

        // Y: Restrict to SKY ONLY (well above grid at y=-5)
        // Spawn between y=2 and y=15
        const y = 2 + Math.random() * 13;

        let radius = 0.5;
        let color = '#ffffff';
        let opacity = 1;
        let rotationSpeed = { x: (Math.random() - 0.5) * 0.2, y: (Math.random() - 0.5) * 0.2 };

        switch (type) {
            case 'planetSmall':
                radius = 0.5 + Math.random() * 0.5;
                color = ['#94a3b8', '#64748b', '#cbd5e1', '#bfdbfe'][Math.floor(Math.random() * 4)];
                break;
            case 'planetLarge':
                radius = 2.0 + Math.random() * 1.5;
                color = ['#1e293b', '#334155', '#475569'][Math.floor(Math.random() * 3)];
                break;
            case 'ringedPlanet':
                radius = 1.2 + Math.random() * 0.6;
                color = '#e2e8f0';
                break;
            case 'moon':
                radius = 0.3 + Math.random() * 0.3;
                color = '#f8fafc';
                break;
            case 'sunGlow':
                radius = 4 + Math.random() * 2;
                color = '#fbbf24'; // Bright Amber
                break;
            case 'asteroid':
                radius = 0.2 + Math.random() * 0.4;
                color = ['#57534e', '#44403c', '#78716c'][Math.floor(Math.random() * 3)];
                break;
            default: break;
        }

        this.activeObjects.push({
            id, type, x, y, z: -50 + (parallax * 20), // Depth -50 to -30
            vx: speed, vy: 0,
            radius, color, opacity, parallax,
            rotation: { x: Math.random() * Math.PI, y: Math.random() * Math.PI },
            rotationSpeed
        });
    }
}

const BackgroundScenery = ({ manager }) => {
    const [objects, setObjects] = useState([]);

    useFrame(() => {
        if (manager.current) {
            if (manager.current.activeObjects.length !== objects.length) {
                setObjects([...manager.current.activeObjects]);
            }
        }
    });

    return (
        <group>
            {/* Ambient light for background objects to ensure they aren't pitch black */}
            <ambientLight intensity={0.4} />
            {objects.map(obj => (
                <BackgroundObjectMesh key={obj.id} obj={obj} manager={manager} />
            ))}
        </group>
    );
};

const BackgroundObjectMesh = ({ obj, manager }) => {
    const meshRef = useRef();

    useFrame(() => {
        if (manager.current) {
            const liveObj = manager.current.activeObjects.find(o => o.id === obj.id);
            if (liveObj && meshRef.current) {
                meshRef.current.position.set(liveObj.x, liveObj.y, liveObj.z);
                meshRef.current.rotation.x = liveObj.rotation.x;
                meshRef.current.rotation.y = liveObj.rotation.y;
            }
        }
    });

    return (
        <group ref={meshRef} position={[obj.x, obj.y, obj.z]}>
            {obj.type === 'ringedPlanet' ? (
                <group>
                    {/* Planet Body */}
                    <mesh>
                        <sphereGeometry args={[obj.radius, 32, 32]} />
                        <meshStandardMaterial color={obj.color} roughness={0.8} metalness={0.2} />
                    </mesh>
                    {/* Rings */}
                    <mesh rotation={[1.2, 0, 0]}>
                        <ringGeometry args={[obj.radius * 1.4, obj.radius * 2.2, 64]} />
                        <meshStandardMaterial color="#cbd5e1" opacity={0.8} transparent side={THREE.DoubleSide} />
                    </mesh>
                </group>
            ) : obj.type === 'sunGlow' ? (
                <group>
                    {/* Solid Core Sun - No Transparency */}
                    <mesh>
                        <sphereGeometry args={[obj.radius, 32, 32]} />
                        <meshBasicMaterial color="#fbbf24" />
                    </mesh>
                    {/* Outer Glow */}
                    <mesh>
                        <sphereGeometry args={[obj.radius * 1.4, 32, 32]} />
                        <meshBasicMaterial color="#f59e0b" opacity={0.3} transparent depthWrite={false} />
                    </mesh>
                </group>
            ) : obj.type === 'asteroid' ? (
                <mesh>
                    <dodecahedronGeometry args={[obj.radius, 0]} /> {/* Low poly look */}
                    <meshStandardMaterial color={obj.color} roughness={0.9} metalness={0.1} flatShading />
                </mesh>
            ) : (
                // Standard Planet / Moon
                <mesh>
                    <sphereGeometry args={[obj.radius, 32, 32]} />
                    <meshStandardMaterial color={obj.color} roughness={0.7} metalness={0.1} />
                </mesh>
            )}
        </group>
    );
};

// --- Boss Manager Class ---
class BossManager {
    constructor() {
        this.mode = 'NORMAL'; // NORMAL, WARNING, BOSS
        this.normalTimer = 0;
        this.bossStageIndex = 0;
        this.bossStages = [
            { bossType: 'DistractionDrone', difficulty: 1 },
            { bossType: 'DoomScroller', difficulty: 1 },
            { bossType: 'DistractionDrone', difficulty: 2 },
            { bossType: 'DoomScroller', difficulty: 2 },
        ];
        this.activeBossId = null;
        this.pendingBossStage = null;
    }

    update(dt, callbacks) {
        const { onWarning, onSpawn, onBossDefeated } = callbacks;

        if (this.mode === 'NORMAL') {
            this.normalTimer += dt;
            if (this.normalTimer >= 30) { // 30s Normal Mode
                this.mode = 'WARNING';
                this.warningTimer = 0;

                // Prepare next stage
                let stage = this.bossStages[this.bossStageIndex];
                if (!stage) {
                    const lastStage = this.bossStages[this.bossStages.length - 1];
                    const extraDiff = this.bossStageIndex - this.bossStages.length + 1;
                    stage = { ...lastStage, difficulty: lastStage.difficulty + extraDiff };
                }
                this.pendingBossStage = stage;

                if (onWarning) onWarning(stage.bossType);
            }
        } else if (this.mode === 'WARNING') {
            this.warningTimer += dt;
            if (this.warningTimer >= 3) { // 3s Warning
                this.spawnNextBoss(onSpawn);
            }
        }
    }

    spawnNextBoss(spawnBossCallback) {
        this.mode = 'BOSS';
        if (this.pendingBossStage && spawnBossCallback) {
            this.activeBossId = spawnBossCallback(this.pendingBossStage.bossType, this.pendingBossStage.difficulty);
        }
    }

    onBossDefeated() {
        this.mode = 'NORMAL';
        this.normalTimer = 0;
        this.activeBossId = null;
        this.bossStageIndex++;
        this.pendingBossStage = null;
    }

    getIsBossActive() {
        return this.mode === 'BOSS' || this.mode === 'WARNING';
    }

    reset() {
        this.mode = 'NORMAL';
        this.normalTimer = 0;
        this.warningTimer = 0;
        this.bossStageIndex = 0;
        this.activeBossId = null;
        this.pendingBossStage = null;
    }
}

// --- Brain PowerUp Component ---
const BrainPowerUp = forwardRef(({ position }, ref) => {
    const group = useRef();

    useFrame((state) => {
        if (group.current) {
            group.current.rotation.y += 0.02;
            group.current.position.y += Math.sin(state.clock.elapsedTime * 2) * 0.002;
        }
    });

    return (
        <group ref={(el) => { group.current = el; if (ref) ref(el); }} position={position}>
            {/* Left Hemisphere */}
            <mesh position={[-0.2, 0, 0]} scale={[0.3, 0.4, 0.5]}>
                <sphereGeometry args={[1, 16, 16]} />
                <meshStandardMaterial color="#ec4899" roughness={0.3} metalness={0.1} />
            </mesh>
            {/* Right Hemisphere */}
            <mesh position={[0.2, 0, 0]} scale={[0.3, 0.4, 0.5]}>
                <sphereGeometry args={[1, 16, 16]} />
                <meshStandardMaterial color="#ec4899" roughness={0.3} metalness={0.1} />
            </mesh>
            {/* Glow */}
            <pointLight distance={3} intensity={2} color="#ec4899" />
        </group>
    );
});

// --- Main Game Component ---

const FocusFlyer = () => {
    // --- State (Rendering) ---
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(0);
    const [isNewHighScore, setIsNewHighScore] = useState(false);
    const [lives, setLives] = useState(3);
    const [weaponLevel, setWeaponLevel] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false); // Track if user has interacted

    const { startMusic, stopMusic, setMusicMode } = useMusic(isMuted);
    const { playExplosion, playLaser, playPickup, playHit, playBossExplosion, playDamage, playEnemyHit, playEnemyShoot, playBossSpawn } = useToneSfx(isMuted);

    // --- Mobile Scroll Lock ---
    useEffect(() => {
        const preventDefault = (e) => e.preventDefault();
        document.addEventListener('touchmove', preventDefault, { passive: false });
        return () => document.removeEventListener('touchmove', preventDefault);
    }, []);

    // Game Mode State for UI
    const [uiGameMode, setUiGameMode] = useState('FLYING'); // 'FLYING', 'BOSS_WARNING', 'BOSS_FIGHT'

    // Lists for Rendering (IDs only)
    const [bullets, setBullets] = useState([]);
    const [enemies, setEnemies] = useState([]);
    const [enemyBullets, setEnemyBullets] = useState([]);
    const [particles, setParticles] = useState([]);

    // --- Refs (Simulation Data) ---
    const bulletsRef = useRef([]); // { id, x, y, z, vx, type }
    const enemiesRef = useRef([]); // { id, x, y, z, type, hp, hitTime, nextShot }
    const enemyBulletsRef = useRef([]); // { id, x, y, z, vx, vy, type }
    const particlesRef = useRef([]); // { id, x, y, z, vx, vy, vz, life, color }

    const playerRef = useRef();
    const meshRefs = useRef({});
    const collectedOrbs = useRef(new Set());

    const frameCount = useRef(0);
    const lastShotTime = useRef(0);
    const isInvulnerable = useRef(false);
    const uniqueIdCounter = useRef(0); // Guaranteed unique IDs

    const bossManager = useRef(new BossManager());
    const backgroundManager = useRef(new BackgroundSceneryManager());

    // Boss Logic Refs
    const gameModeRef = useRef('FLYING');
    // const nextBossTimeRef = useRef(45); // Removed
    const bossTimerRef = useRef(0);
    const bossHealthBarRef = useRef(null);
    const bossMaxHpRef = useRef(60); // Track max HP for current boss
    const gameStartTimeRef = useRef(null); // Track when game actually started

    // Load High Score on Mount
    useEffect(() => {
        setHighScore(getHighScore(GAME_KEYS.FOCUS));
    }, []);

    const startGame = () => {
        resetGame();
        setGameStarted(true);
        startMusic();
        playSfx('powerup');
    };

    // --- Helpers ---
    const playSfx = (name) => {
        if (isMuted) return;
        switch (name) {
            case 'damage': playDamage(); break;
            case 'enemy_hit': playEnemyHit(); break;
            case 'enemy_shoot': playEnemyShoot(); break;
            case 'boss_spawn': playBossSpawn(); break;
            case 'enemy_explode': playExplosion(); break;
            case 'powerup': playPickup(); break;
            case 'health': playPickup(); break;
            case 'boss_defeat': playBossExplosion(); break;
            default: break;
        }
    };

    const getUniqueId = (prefix = '') => {
        uniqueIdCounter.current += 1;
        return `${prefix}${Date.now()}_${uniqueIdCounter.current}`;
    };

    const spawnParticleExplosion = (x, y, z, color, count = 8) => {
        const newParticles = [];
        for (let i = 0; i < count; i++) {
            newParticles.push({
                id: getUniqueId('p'),
                x, y, z,
                vx: (Math.random() - 0.5) * 0.3,
                vy: (Math.random() - 0.5) * 0.3,
                vz: (Math.random() - 0.5) * 0.3,
                life: 1.0,
                color
            });
        }
        particlesRef.current = [...particlesRef.current, ...newParticles];
        setParticles(prev => [...prev, ...newParticles]);
    };

    const resetGame = () => {
        setScore(0);
        setLives(3);
        setWeaponLevel(0);
        setGameOver(false);
        setUiGameMode('FLYING');
        setGameStarted(false); // Reset gameStarted state
        setIsNewHighScore(false); // Reset new high score flag

        setBullets([]);
        setEnemies([]);
        setEnemyBullets([]);
        setParticles([]);

        bulletsRef.current = [];
        enemiesRef.current = [];
        enemyBulletsRef.current = [];
        particlesRef.current = [];
        collectedOrbs.current.clear();
        isInvulnerable.current = false;
        meshRefs.current = {};
        uniqueIdCounter.current = 0; // Reset ID counter on game reset

        gameModeRef.current = 'FLYING';
        bossManager.current.reset();
        bossTimerRef.current = 0;
        gameStartTimeRef.current = null; // Reset start time
    };

    // --- Game Loop ---
    const GameLogic = () => {
        const { viewport, mouse } = useThree();

        useFrame((state, delta) => {
            if (gameOver || !gameStarted) return;

            const time = state.clock.elapsedTime;

            // Update Background Scenery
            if (backgroundManager.current) {
                backgroundManager.current.update(delta, bossManager.current.mode !== 'NORMAL');
            }

            // Initialize start time on first frame of gameplay
            if (gameStartTimeRef.current === null) {
                gameStartTimeRef.current = time;
            }

            const gameTime = time - gameStartTimeRef.current;
            frameCount.current++;

            // 1. Player Movement
            if (playerRef.current) {
                const targetX = (mouse.x * viewport.width) / 2;
                const targetY = (mouse.y * viewport.height) / 2;
                playerRef.current.position.x = THREE.MathUtils.lerp(playerRef.current.position.x, targetX, PLAYER_SPEED);
                playerRef.current.position.y = THREE.MathUtils.lerp(playerRef.current.position.y, targetY, PLAYER_SPEED);

                playerRef.current.rotation.z = -playerRef.current.position.x * 0.15;
                playerRef.current.rotation.x = -playerRef.current.position.y * 0.1;

                playerRef.current.visible = isInvulnerable.current ? Math.sin(time * 20) > 0 : true;
            }

            // 2. Player Shooting
            const fireRate = weaponLevel === 2 ? 0.1 : 0.2;
            if (time - lastShotTime.current > fireRate) {
                lastShotTime.current = time;
                const newBullets = [];
                const x = playerRef.current?.position.x || 0;
                const y = playerRef.current?.position.y || 0;
                const type = weaponLevel === 2 ? 'LASER' : 'NORMAL';

                if (weaponLevel === 0) {
                    newBullets.push({ id: getUniqueId('b'), x, y, z: 0, vx: 0, type });
                } else if (weaponLevel === 1) {
                    newBullets.push({ id: getUniqueId('b'), x: x - 0.25, y, z: 0, vx: 0, type });
                    newBullets.push({ id: getUniqueId('b'), x: x + 0.25, y, z: 0, vx: 0, type });
                } else if (weaponLevel === 2) {
                    newBullets.push({ id: getUniqueId('b'), x, y, z: 0, vx: 0, type });
                } else if (weaponLevel === 3) {
                    newBullets.push({ id: getUniqueId('b'), x: x - 0.3, y, z: 0, vx: -0.2, type });
                    newBullets.push({ id: getUniqueId('b'), x, y, z: 0, vx: 0, type });
                    newBullets.push({ id: getUniqueId('b'), x: x + 0.3, y, z: 0, vx: 0.2, type });
                }

                bulletsRef.current = [...bulletsRef.current, ...newBullets];
                setBullets(prev => [...prev, ...newBullets]);
                // playSfx(weaponLevel > 0 ? 'shoot_upgrade' : 'shoot'); // Muted per request
            }

            // 3. Boss Manager Update
            // Use state.delta (frame delta) instead of clock.getDelta() which can be unreliable if called multiple times
            bossManager.current.update(delta, {
                onWarning: (bossType) => {
                    setUiGameMode('BOSS_WARNING');
                    playSfx('boss_spawn');
                    setMusicMode('BOSS'); // Switch to Boss Music
                    bossManager.current.pendingBossTypeForUi = bossType;
                },
                onSpawn: (bossType, difficulty) => {
                    const bossId = getUniqueId('boss');
                    let newBossParts = [];

                    if (bossType === 'DistractionDrone') {
                        newBossParts = [
                            { id: `${bossId}_top`, type: 'BOSS_TOP', x: 0, y: 2.5, z: -8, hp: 20 * difficulty, maxHp: 20 * difficulty, hitTime: 0, nextShot: time + 1, bossType: 'DistractionDrone' },
                            { id: `${bossId}_center`, type: 'BOSS_CENTER', x: 0, y: 0, z: -8, hp: 20 * difficulty, maxHp: 20 * difficulty, hitTime: 0, nextShot: time + 2, bossType: 'DistractionDrone' },
                            { id: `${bossId}_bottom`, type: 'BOSS_BOTTOM', x: 0, y: -2.5, z: -8, hp: 20 * difficulty, maxHp: 20 * difficulty, hitTime: 0, nextShot: time + 3, bossType: 'DistractionDrone' }
                        ];
                    } else if (bossType === 'DoomScroller') {
                        newBossParts = [{
                            id: bossId,
                            type: 'DOOM_SCROLLER',
                            x: 0, y: 1.5, z: -8, // Lowered from 2.5 to 1.5
                            hp: 50 * difficulty,
                            maxHp: 50 * difficulty,
                            hitTime: 0,
                            state: 'IDLE', // IDLE, FALLING_RECTANGLES
                            stateTimer: time + 2, // Initial delay
                            difficulty: difficulty,
                            bossType: 'DoomScroller'
                        }];
                    }

                    enemiesRef.current = [...enemiesRef.current, ...newBossParts];
                    setEnemies(prev => [...prev, ...newBossParts]);

                    // Set Game Mode Refs correctly
                    gameModeRef.current = 'BOSS_FIGHT';
                    setUiGameMode('BOSS_FIGHT');

                    // Calculate and set Max HP for health bar
                    const totalHp = newBossParts.reduce((sum, part) => sum + part.hp, 0);
                    bossMaxHpRef.current = totalHp;

                    // Clear existing normal enemies to focus on boss
                    const bossIds = new Set(newBossParts.map(p => p.id));
                    enemiesRef.current = enemiesRef.current.filter(e => bossIds.has(e.id));
                    setEnemies(prev => prev.filter(e => bossIds.has(e.id)));

                    return bossId;
                }
            });

            // 4. Boss Behavior (Doom Scroller)
            enemiesRef.current.forEach(enemy => {
                if (enemy.type === 'DOOM_SCROLLER') {
                    // Movement: Drift left/right
                    enemy.x = Math.sin(time * 0.5) * 1.5;

                    // State Machine
                    if (time > enemy.stateTimer) {
                        if (enemy.state === 'IDLE') {
                            // Pick next attack
                            enemy.state = 'FALLING_RECTANGLES';
                            enemy.stateTimer = time + 10; // Duration of attack sequence
                            enemy.nextSpawnTime = time;
                        } else {
                            // Attack finished, go back to IDLE
                            enemy.state = 'IDLE';
                            enemy.stateTimer = time + 2; // Cooldown
                        }
                    }

                    // Execute Attacks
                    if (enemy.state === 'FALLING_RECTANGLES') {
                        // 1s Active, 1s Pause
                        if (time % 2 < 1) {
                            if (time > enemy.nextSpawnTime) {
                                // Spawn a falling rectangle
                                const x = (Math.random() - 0.5) * 10; // Random X within bounds
                                const rect = {
                                    id: getUniqueId('rect'),
                                    x: x,
                                    y: 10, // Start above screen
                                    z: 0,
                                    vx: 0,
                                    vy: -0.15, // Fall speed
                                    type: 'FALLING_RECT'
                                };
                                enemyBulletsRef.current.push(rect);
                                setEnemyBullets(prev => [...prev, rect]);

                                enemy.nextSpawnTime = time + 0.5; // Spawn every 0.5s during active phase
                                playSfx('enemy_shoot');
                            }
                        }
                    } else if (enemy.state === 'FEED_BARRAGE') {
                        // Spawn falling tiles
                        if (frameCount.current % 10 === 0) { // Rate
                            const lanes = [-2, -1, 0, 1, 2];
                            const safeLane = Math.floor(Math.random() * 5);
                            lanes.forEach((laneX, i) => {
                                if (i !== safeLane) {
                                    const bullet = {
                                        id: getUniqueId('eb'),
                                        x: laneX * 1.2, // Lane width
                                        y: 4,
                                        z: 0,
                                        vx: 0,
                                        vy: -0.15 - (enemy.difficulty * 0.02), // Speed scales with difficulty
                                        type: 'FEED_TILE'
                                    };
                                    enemyBulletsRef.current.push(bullet);
                                    setEnemyBullets(prev => [...prev, bullet]);
                                }
                            });
                        }
                    } else if (enemy.state === 'NOTIFICATION_STORM') {
                        // Burst logic
                        if (frameCount.current % 60 === 0) { // Fire every second during attack
                            const count = 8 + enemy.difficulty;
                            for (let i = 0; i < count; i++) {
                                const angle = (i / count) * Math.PI * 2;
                                const speed = 0.15 + (enemy.difficulty * 0.01);
                                const bullet = {
                                    id: getUniqueId('eb'),
                                    x: enemy.x,
                                    y: enemy.y,
                                    z: 0,
                                    vx: Math.cos(angle) * speed,
                                    vy: Math.sin(angle) * speed,
                                    type: 'NOTIFICATION'
                                };
                                enemyBulletsRef.current.push(bullet);
                                setEnemyBullets(prev => [...prev, bullet]);
                            }
                            playSfx('enemy_shoot');
                        }
                    } else if (enemy.state === 'LASER_ATTACK') {
                        // Logic handled by the bullet update mostly, but we can trigger the phase shift here
                        if (enemy.laserPhase === 'TELEGRAPH' && time > enemy.laserTimer) {
                            enemy.laserPhase = 'FIRE';
                            playSfx('enemy_shoot'); // Or a laser sound
                            // Find the laser bullet and update its phase
                            enemyBulletsRef.current.forEach(b => {
                                if (b.type === 'LASER_BEAM' && b.phase === 'TELEGRAPH') {
                                    b.phase = 'FIRE';
                                    b.vy = 0.5; // Scroll speed
                                }
                            });
                        }
                    }
                }
            });

            // 5. Spawning (Normal Enemies) - Only when NOT in Boss Mode
            if (!bossManager.current.getIsBossActive() && frameCount.current % SPAWN_RATE === 0) {
                const x = (Math.random() - 0.5) * viewport.width;
                const y = (Math.random() - 0.5) * viewport.height;
                let type = 'ENEMY';
                if (Math.random() < ORB_CHANCE) type = 'ORB';
                else if (Math.random() < HEALTH_PICKUP_CHANCE) type = 'HEALTH';

                const newEnemy = {
                    id: getUniqueId('e'),
                    x, y, z: -50,
                    type,
                    hp: 2,
                    hitTime: 0,
                    nextShot: time + 1 + Math.random()
                };
                enemiesRef.current = [...enemiesRef.current, newEnemy];
                setEnemies(prev => [...prev, newEnemy]);
            }

            // 5. Update Bullets
            const activeBullets = [];
            const deadBulletIds = new Set();
            bulletsRef.current.forEach(b => {
                b.z -= BULLET_SPEED;
                b.x += (b.vx || 0);

                const mesh = meshRefs.current[b.id];
                if (mesh) mesh.position.set(b.x, b.y, b.z);

                if (b.z > -60) activeBullets.push(b);
                else deadBulletIds.add(b.id);
            });
            bulletsRef.current = activeBullets;

            // 6. Update Enemies (and Boss Parts)
            const activeEnemies = [];
            const deadEnemyIds = new Set();
            let bossPartsAlive = 0;

            enemiesRef.current.forEach(e => {
                const isBoss = e.type.startsWith('BOSS') || e.type === 'DOOM_SCROLLER';
                if (isBoss) bossPartsAlive++;

                // Movement
                if (isBoss) {
                    // Boss Hover
                    e.y += Math.sin(time * 2 + (e.id.length || 0)) * 0.005; // Use length as seed since ID is string
                    // Slight sway
                    e.x += Math.cos(time + (e.id.length || 0)) * 0.01;
                } else {
                    // Normal Enemy Move
                    e.z += ENEMY_SPEED;
                    e.y += Math.sin(time * 2 + (e.id.length || 0)) * 0.005;
                }

                const mesh = meshRefs.current[e.id];
                if (mesh) {
                    mesh.position.set(e.x, e.y, e.z);

                    // Flash Effect Logic (Recursive)
                    const flashColor = 0xffffff;
                    const baseColor = 0x000000;
                    const isFlashing = e.hitTime > 0 && time - e.hitTime < 0.2;

                    const updateMaterial = (obj) => {
                        if (obj.isMesh && obj.material && obj.material.emissive) {
                            obj.material.emissive.setHex(isFlashing ? flashColor : baseColor);
                            obj.material.emissiveIntensity = isFlashing ? 1 : 0;
                        }
                        if (obj.children) {
                            obj.children.forEach(updateMaterial);
                        }
                    };
                    updateMaterial(mesh);
                }

                // Shooting Logic
                if (time > e.nextShot) {
                    if (e.type === 'ENEMY') {
                        const newBullet = { id: getUniqueId('eb'), x: e.x, y: e.y, z: e.z + 1, vx: 0, type: 'NORMAL' };
                        enemyBulletsRef.current.push(newBullet);
                        setEnemyBullets(prev => [...prev, newBullet]);
                        e.nextShot = time + 2 + Math.random() * 2;
                        if (Math.random() < 0.3) playSfx('enemy_shoot'); // Don't play every time to avoid spam
                    } else if (e.type === 'BOSS_TOP') {
                        // Spread Shot
                        [-0.1, 0, 0.1].forEach(vx => {
                            const newBullet = { id: getUniqueId('bb'), x: e.x, y: e.y, z: e.z + 1, vx, type: 'BOSS_BUBBLE' };
                            enemyBulletsRef.current.push(newBullet);
                            setEnemyBullets(prev => [...prev, newBullet]);
                        });
                        e.nextShot = time + 2;
                    } else if (e.type === 'BOSS_CENTER') {
                        // Direct Shot
                        const newBullet = { id: getUniqueId('bb'), x: e.x, y: e.y, z: e.z + 1, vx: 0, type: 'BOSS_LASER' };
                        enemyBulletsRef.current.push(newBullet);
                        setEnemyBullets(prev => [...prev, newBullet]);
                        e.nextShot = time + 1.5;
                    } else if (e.type === 'BOSS_BOTTOM') {
                        // Homing Shot (Simulated by aiming at player x)
                        const dx = playerRef.current ? playerRef.current.position.x - e.x : 0;
                        const vx = dx * 0.05;
                        const newBullet = { id: getUniqueId('bb'), x: e.x, y: e.y, z: e.z + 1, vx, type: 'BOSS_HASHTAG' };
                        enemyBulletsRef.current.push(newBullet);
                        setEnemyBullets(prev => [...prev, newBullet]);
                        e.nextShot = time + 2.5;
                    }
                }

                // Collision: Bullet vs Enemy
                let isDead = false;
                bulletsRef.current.forEach(b => {
                    if (deadBulletIds.has(b.id)) return;

                    const dx = b.x - e.x;
                    const dy = b.y - e.y;
                    const dz = b.z - e.z;
                    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

                    // Larger hitbox for boss parts
                    const hitDist = isBoss ? 2.5 : 1.5; // Increased boss hitbox slightly

                    if (dist < hitDist) {
                        if (e.type === 'ENEMY' || isBoss) {
                            e.hp -= 1;
                            e.hitTime = time;
                            deadBulletIds.add(b.id);
                            spawnParticleExplosion(e.x, e.y, e.z, '#facc15', 3);
                            playSfx('enemy_hit');

                            if (e.hp <= 0) {
                                isDead = true;
                                setScore(s => s + (isBoss ? 200 : 50)); // 50 pts enemy, 200 pts boss part
                                spawnParticleExplosion(e.x, e.y, e.z, '#ef4444', 10);
                                playSfx('enemy_explode');
                            }
                        }
                    }
                });

                // Collision: Player vs Enemy
                if (playerRef.current) {
                    const pdx = playerRef.current.position.x - e.x;
                    const pdy = playerRef.current.position.y - e.y;
                    const pdz = playerRef.current.position.z - e.z;
                    const pDist = Math.sqrt(pdx * pdx + pdy * pdy + pdz * pdz);

                    if (pDist < 1.5) {
                        if (e.type === 'ORB') {
                            if (!collectedOrbs.current.has(e.id)) {
                                collectedOrbs.current.add(e.id);
                                setWeaponLevel(l => {
                                    if (l >= 3) {
                                        setScore(s => s + 100);
                                        return l;
                                    }
                                    return l + 1;
                                });
                                isDead = true;
                                playSfx('powerup');
                            }
                        } else if (e.type === 'HEALTH') {
                            if (!collectedOrbs.current.has(e.id)) {
                                collectedOrbs.current.add(e.id);
                                setLives(h => Math.min(3, h + 1));
                                isDead = true;
                                playSfx('health');
                            }
                        } else if (e.type === 'ENEMY' || isBoss) {
                            if (!isInvulnerable.current) {
                                setLives(h => {
                                    const newH = h - 1;
                                    if (newH <= 0) {
                                        setGameOver(true);
                                        setGameStarted(false);

                                        const isNew = trySetHighScore(GAME_KEYS.FOCUS, score);
                                        setIsNewHighScore(isNew);
                                        if (isNew) {
                                            setHighScore(score);
                                        } else {
                                            setHighScore(getHighScore(GAME_KEYS.FOCUS));
                                        }

                                        setMusicMode('NORMAL'); // Reset music
                                        stopMusic(); // Or fade out
                                    }
                                    return newH;
                                });
                                setWeaponLevel(0);
                                isInvulnerable.current = true;
                                setTimeout(() => isInvulnerable.current = false, 2000);
                                spawnParticleExplosion(playerRef.current.position.x, playerRef.current.position.y, playerRef.current.position.z, '#3b82f6', 20);
                                playSfx('damage');
                            }
                        }
                    }
                }

                if (!isDead && (isBoss || e.z < 10)) activeEnemies.push(e);
                else deadEnemyIds.add(e.id);
            });
            enemiesRef.current = activeEnemies;

            // Update Boss Health Bar
            if (gameModeRef.current === 'BOSS_FIGHT' && bossHealthBarRef.current) {
                const maxHp = bossMaxHpRef.current;
                let currentBossHp = 0;
                activeEnemies.forEach(e => {
                    if (e.type.startsWith('BOSS') || e.type === 'DOOM_SCROLLER') currentBossHp += e.hp;
                });
                const hpPercent = Math.max(0, (currentBossHp / maxHp) * 100);
                bossHealthBarRef.current.style.width = `${hpPercent}%`;
            }

            // Boss Victory Check
            if (gameModeRef.current === 'BOSS_FIGHT' && bossPartsAlive === 0) {
                // Boss Defeated!
                gameModeRef.current = 'FLYING';
                setUiGameMode('FLYING');
                bossManager.current.onBossDefeated();
                // nextBossTimeRef.current = gameTime + 45; // Handled by BossManager
                setScore(s => s + 500); // Victory Bonus
                playSfx('boss_defeat');
                setMusicMode('NORMAL'); // Switch back to Normal Music
            }

            // 7. Update Enemy Bullets
            const activeEnemyBullets = [];
            const deadEnemyBulletIds = new Set();
            enemyBulletsRef.current.forEach(b => {
                if (b.type === 'FALLING_RECT' || b.type === 'FEED_TILE') {
                    b.y += b.vy; // Fall down
                } else {
                    b.z += ENEMY_BULLET_SPEED;
                    b.x += (b.vx || 0);
                }

                const mesh = meshRefs.current[b.id];
                if (mesh) mesh.position.set(b.x, b.y, b.z);

                // Collision Player
                if (playerRef.current) {
                    // Check collision based on type
                    let hit = false;
                    if (b.type === 'FALLING_RECT' || b.type === 'FEED_TILE') {
                        // Box collision for rect
                        // Size [2, 3, 0.5]
                        const dx = Math.abs(playerRef.current.position.x - b.x);
                        const dy = Math.abs(playerRef.current.position.y - b.y);
                        if (dx < 1.0 && dy < 1.5) { // Half extents
                            hit = true;
                        }
                    } else {
                        const dx = playerRef.current.position.x - b.x;
                        const dy = playerRef.current.position.y - b.y;
                        const dz = playerRef.current.position.z - b.z;
                        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
                        if (dist < 1.0) hit = true;
                    }

                    if (hit) {
                        if (!isInvulnerable.current) {
                            setLives(h => {
                                const newH = h - 1;
                                if (newH <= 0) {
                                    setGameOver(true);
                                    setGameStarted(false);

                                    const isNew = trySetHighScore(GAME_KEYS.FOCUS, score);
                                    setIsNewHighScore(isNew);
                                    if (isNew) {
                                        setHighScore(score);
                                    } else {
                                        setHighScore(getHighScore(GAME_KEYS.FOCUS));
                                    }

                                    setMusicMode('NORMAL'); // Reset music
                                    stopMusic(); // Or fade out
                                }
                                return newH;
                            });
                            setWeaponLevel(0);
                            isInvulnerable.current = true;
                            setTimeout(() => isInvulnerable.current = false, 2000);
                            spawnParticleExplosion(playerRef.current.position.x, playerRef.current.position.y, playerRef.current.position.z, '#ef4444', 10);
                            playSfx('damage');
                        }
                        if (b.type !== 'FALLING_RECT' && b.type !== 'FEED_TILE') deadEnemyBulletIds.add(b.id); // Rects don't disappear on hit? Maybe they should. Let's make them disappear.
                        else deadEnemyBulletIds.add(b.id);
                        return;
                    }
                }

                if (b.type === 'FALLING_RECT' || b.type === 'FEED_TILE') {
                    if (b.y < -10) deadEnemyBulletIds.add(b.id); // Remove when off screen bottom
                    else activeEnemyBullets.push(b);
                } else {
                    if (b.z < 10) activeEnemyBullets.push(b);
                    else deadEnemyBulletIds.add(b.id);
                }
            });
            enemyBulletsRef.current = activeEnemyBullets;

            // 8. Update Particles
            const activeParticles = [];
            const deadParticleIds = new Set();
            particlesRef.current.forEach(p => {
                p.life -= 0.05;
                p.x += p.vx;
                p.y += p.vy;
                p.z += p.vz;

                const mesh = meshRefs.current[p.id];
                if (mesh) {
                    mesh.position.set(p.x, p.y, p.z);
                    mesh.scale.setScalar(p.life);
                    mesh.material.opacity = p.life;
                }

                if (p.life > 0) activeParticles.push(p);
                else deadParticleIds.add(p.id);
            });
            particlesRef.current = activeParticles;

            // 9. Sync State
            if (deadBulletIds.size > 0) {
                setBullets(prev => prev.filter(b => !deadBulletIds.has(b.id)));
                bulletsRef.current = bulletsRef.current.filter(b => !deadBulletIds.has(b.id));
            }
            if (deadEnemyIds.size > 0) {
                setEnemies(prev => prev.filter(e => !deadEnemyIds.has(e.id)));
            }

            if (deadEnemyBulletIds.size > 0) {
                setEnemyBullets(prev => prev.filter(b => !deadEnemyBulletIds.has(b.id)));
            }

            if (deadParticleIds.size > 0) {
                setParticles(prev => prev.filter(p => !deadParticleIds.has(p.id)));
            }
        });
        return null;
    };

    return (
        <div className="absolute inset-0 w-full h-full bg-slate-900 overflow-hidden touch-none overscroll-none">
            <Canvas camera={{ position: [0, 2, 5], fov: 60 }}>
                <Suspense fallback={null}>
                    <color attach="background" args={['#0f172a']} />
                    <Stars radius={100} depth={50} count={5000} factor={2} saturation={0} fade speed={0.5} />
                    <ambientLight intensity={0.5} />
                    <pointLight position={[10, 10, 10]} intensity={1} />
                    <gridHelper args={[100, 50, '#06b6d4', '#1e293b']} position={[0, -5, 0]} />

                    <BackgroundScenery manager={backgroundManager} />

                    <PlayerShip ref={playerRef} position={[0, 0, 0]} />

                    {bullets.map(b => (
                        <Bullet
                            key={b.id}
                            type={b.type}
                            ref={(el) => { if (el) meshRefs.current[b.id] = el; else delete meshRefs.current[b.id]; }}
                        />
                    ))}

                    {enemies.map(e => (
                        e.type === 'ORB' ?
                            <PlusSign
                                key={e.id}
                                color="#ccff00"
                                outline={true}
                                ref={(el) => { if (el) meshRefs.current[e.id] = el; else delete meshRefs.current[e.id]; }}
                            /> :
                            e.type === 'HEALTH' ?
                                <PlusSign
                                    key={e.id}
                                    color="#22c55e"
                                    ref={(el) => { if (el) meshRefs.current[e.id] = el; else delete meshRefs.current[e.id]; }}
                                /> :
                                e.type.startsWith('BOSS') ?
                                    <BossPart
                                        key={e.id}
                                        type={e.type}
                                        ref={(el) => { if (el) meshRefs.current[e.id] = el; else delete meshRefs.current[e.id]; }}
                                    /> :
                                    e.type === 'DOOM_SCROLLER' ?
                                        <DoomScroller
                                            key={e.id}
                                            difficulty={e.difficulty}
                                            ref={(el) => { if (el) meshRefs.current[e.id] = el; else delete meshRefs.current[e.id]; }}
                                        /> :
                                        <EnemyShip
                                            key={e.id}
                                            ref={(el) => { if (el) meshRefs.current[e.id] = el; else delete meshRefs.current[e.id]; }}
                                        />
                    ))}

                    {enemyBullets.map(b => (
                        b.type === 'FALLING_RECT' ?
                            <mesh
                                key={b.id}
                                ref={(el) => { if (el) meshRefs.current[b.id] = el; else delete meshRefs.current[b.id]; }}
                                position={[b.x, b.y, b.z]}
                            >
                                <boxGeometry args={[2, 3, 0.5]} />
                                <meshStandardMaterial
                                    color="#3b82f6"
                                    emissive="#3b82f6"
                                    emissiveIntensity={0.5}
                                    transparent
                                    opacity={0.8}
                                />
                            </mesh> :

                            <EnemyBullet
                                key={b.id}
                                type={b.type}
                                ref={(el) => { if (el) meshRefs.current[b.id] = el; else delete meshRefs.current[b.id]; }}
                            />
                    ))}

                    {particles.map(p => (
                        <Particle
                            key={p.id}
                            color={p.color}
                            ref={(el) => { if (el) meshRefs.current[p.id] = el; else delete meshRefs.current[p.id]; }}
                        />
                    ))}

                    <GameLogic />
                </Suspense>
            </Canvas>

            {/* HUD */}
            <div className="absolute top-4 left-0 w-full px-6 flex justify-between items-start pointer-events-none z-10">
                {/* Health & Score */}
                <div className="flex flex-col gap-2">
                    <div className="flex gap-1">
                        {[...Array(3)].map((_, i) => (
                            <Heart
                                key={i}
                                className={`w-6 h-6 ${i < lives ? 'text-green-500 fill-green-500' : 'text-gray-800 fill-gray-800'}`}
                            />
                        ))}
                    </div>
                    <div className="text-2xl font-black text-cyan-400 font-mono">
                        {score.toString().padStart(6, '0')}
                    </div>
                </div>

                {/* Mute Button */}
                <button
                    onClick={() => setIsMuted(!isMuted)}
                    className="p-2 bg-slate-800/80 rounded-full text-cyan-400 hover:bg-slate-700 pointer-events-auto"
                >
                    {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
                </button>



                {/* Boss Warning */}
                {uiGameMode === 'BOSS_WARNING' && (
                    <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center animate-pulse w-full px-4">
                        <h2 className="text-4xl sm:text-6xl font-black text-red-600 tracking-tighter shadow-black drop-shadow-lg">WARNING</h2>
                        <p className="text-red-400 text-sm sm:text-xl font-mono mt-2">
                            {bossManager.current.pendingBossTypeForUi === 'DoomScroller' ? 'DOOM SCROLLER DETECTED' : 'DISTRACTION DRONE APPROACHING'}
                        </p>
                    </div>
                )}

                {/* Boss HP Bar (Aggregate) */}
                {uiGameMode === 'BOSS_FIGHT' && (
                    <div className="absolute top-24 left-1/2 -translate-x-1/2 w-64 h-4 bg-slate-800/80 rounded-full border border-red-500/50 overflow-hidden">
                        <div
                            ref={bossHealthBarRef}
                            className="h-full bg-gradient-to-r from-red-600 to-red-400 transition-all duration-200"
                            style={{ width: '100%' }}
                        />
                    </div>
                )}
            </div>

            {/* Weapon Bar */}
            <div className="absolute bottom-36 left-1/2 -translate-x-1/2 flex gap-1 bg-slate-900/80 p-2 rounded-lg border border-slate-700 pointer-events-none">
                {WEAPON_LEVELS.map((u, i) => (
                    <div
                        key={u}
                        className={`px-2 py-1 rounded text-[10px] sm:text-xs font-bold transition-all ${i === weaponLevel
                            ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg scale-110'
                            : 'bg-slate-800 text-slate-500'
                            }`}
                    >
                        {u}
                    </div>
                ))}
            </div>

            {/* Start Screen */}
            {!gameStarted && !gameOver && (
                <div className="absolute inset-0 bg-slate-900/95 flex flex-col items-center justify-center z-50">
                    <h1 className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600 mb-2 tracking-tighter">
                        FOCUS FLYER
                    </h1>
                    <p className="text-slate-400 text-lg mb-8 font-mono">NEURAL NETWORK DEFENSE SYSTEM</p>

                    <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 max-w-md w-full mb-8">
                        <h3 className="text-cyan-400 font-bold mb-4 flex items-center gap-2">
                            <Rocket size={20} /> MISSION BRIEFING
                        </h3>
                        <ul className="space-y-3 text-slate-300 text-sm">
                            <li className="flex items-start gap-2">
                                <span className="text-cyan-500">➜</span>
                                <span><strong>DRAG</strong> to pilot your ship. Firing is automatic.</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-cyan-500">➜</span>
                                <span><strong>DODGE</strong> incoming emails and distractions.</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-cyan-500">➜</span>
                                <span>Collect <strong className="text-yellow-400">+</strong> symbols to upgrade weapons.</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-cyan-500">➜</span>
                                <span>Survive the <strong>DISTRACTION DRONE</strong>.</span>
                            </li>
                        </ul>
                    </div>

                    <div className="flex flex-col items-center gap-4">
                        <button
                            onClick={startGame}
                            className="px-12 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-black text-xl rounded-full shadow-lg shadow-cyan-500/20 transform hover:scale-105 transition-all flex items-center gap-3"
                        >
                            <Rocket size={24} /> LIFT OFF
                        </button>

                        <div className="flex flex-col items-center gap-2">
                            <div className="text-cyan-400 font-mono text-lg flex items-center gap-2">
                                <Trophy size={20} />
                                <span>BEST: {highScore}</span>
                            </div>
                        </div>

                        <label className="flex items-center gap-2 text-slate-400 cursor-pointer hover:text-white transition-colors">
                            <input
                                type="checkbox"
                                checked={!isMuted}
                                onChange={() => setIsMuted(!isMuted)}
                                className="w-4 h-4 rounded border-slate-600 text-cyan-500 focus:ring-cyan-500 bg-slate-800"
                            />
                            <span className="text-sm font-bold">ENABLE SOUND</span>
                        </label>
                    </div>
                </div>
            )}

            {/* Game Over */}
            {gameOver && (
                <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center pointer-events-auto z-50">
                    <h2 className="text-5xl font-black text-red-500 mb-4 tracking-tighter">GAME OVER</h2>
                    <p className="text-cyan-400 text-xl mb-4 font-mono">SCORE: {score}</p>

                    {isNewHighScore ? (
                        <div className="mb-8 flex flex-col items-center animate-bounce">
                            <span className="text-yellow-400 font-bold text-xl tracking-widest flex items-center gap-2">
                                <Trophy size={24} /> NEW HIGH SCORE!
                            </span>
                        </div>
                    ) : (
                        <div className="text-slate-400 mb-8 font-mono flex items-center gap-4">
                            <span>Best: <span className="text-cyan-400">{highScore}</span></span>
                        </div>
                    )}
                    <button
                        onClick={resetGame}
                        className="px-8 py-3 bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full transition-all transform hover:scale-105"
                    >
                        RETRY MISSION
                    </button>
                </div>
            )}
        </div>
    );
};

export default FocusFlyer;
